import React, { useEffect, useState } from 'react';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';

import EcFilterInput from '~/components/Shared/EcFilterInput/EcFilterInput';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Paginate } from '~/eds/src/components/Paginate/Paginate';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from '~/ui/components/Box/Box';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Link from '~/ui/components/Link/Link';
import Text from '~/ui/components/Text/Text';
import { copyToClipboard } from '~/utils/helper.utils';
import { getWorkflowPath } from '~/utils/workflow.utils';

import styles from './WorkflowTemplatesList.styles';

const PAGE_SIZE = 10;

const WorkflowTemplatesList = ({ classes, currentUser, templatesData }) => {
  const intl = useIntl();
  const [copiedTooltip, showCopiedTooltip] = useState(false);
  const [page, setPage] = useState(1);

  const [filterValue, setFilterValue] = useState('');

  // reset the page when the filter value changes
  useEffect(() => {
    setPage(1);
  }, [filterValue]);

  const filteredItems = templatesData.filter((item) =>
    item.name.toLowerCase().includes(filterValue.toLowerCase()),
  );

  function handleOnCopyAction(item) {
    showCopiedTooltip(true);
    const path = getWorkflowPath(item);
    const linkConstructed = `${window.location.origin}/${currentUser.client_config.sub_domain}${path}`;
    copyToClipboard(linkConstructed, null, null, handleCopiedTooltip);
  }

  function handleCopiedTooltip() {
    setTimeout(() => {
      showCopiedTooltip(false);
    }, 2000);
  }

  return (
    <FlexLayout alignItems="center" flexDirection="column" space={6}>
      <Box className={classes.templatesFilter}>
        <EcFilterInput
          autoFocus
          value={filterValue}
          onChange={(filterValue) => setFilterValue(filterValue)}
          onClearSearch={() => setFilterValue('')}
          placeholder={intl.formatMessage({
            id: 'WorkflowTemplatesList.searchPlaceholder',
            defaultMessage: 'Search workflows...',
            description: 'Placeholder text for workflow search input',
          })}
        />
      </Box>
      <Box className={classes.templatesList}>
        {filteredItems.length ? (
          filteredItems
            .slice(PAGE_SIZE * (page - 1), PAGE_SIZE * page)
            .map((item) => {
              const path = getWorkflowPath(item);

              return (
                <FlexLayout
                  key={`workflow-${item.id}`}
                  alignItems="center"
                  className={classes.listItem}
                  justifyContent="space-between"
                  p={4}
                  space={4}
                >
                  <FlexLayout flexDirection="column" sx={{ maxWidth: '50%' }}>
                    <FlexLayout alignItems="center" space={2}>
                      {item.wssDesignated && (
                        <Icon
                          data-testid="wss-designated-icon"
                          icon="workday"
                          color="gray-600"
                          size="s"
                        />
                      )}

                      <Link to={path}>{item.name}</Link>
                    </FlexLayout>

                    {item.description && (
                      <Text
                        color="gray-600"
                        variant="xs-dense"
                        sx={{ wordBreak: 'break-word' }}
                      >
                        {item.description}
                      </Text>
                    )}
                  </FlexLayout>
                  <FlexLayout alignItems="center" p={1} space={4}>
                    <FlexLayout pl={4}>
                      <span data-tip data-for={item.permanentLinkId}>
                        <IconButton
                          icon="link"
                          size="s"
                          tooltip={
                            copiedTooltip
                              ? intl.formatMessage({
                                  id: 'WorkflowTemplatesList.linkCopiedTooltip',
                                  defaultMessage: 'Intake Form Link copied',
                                  description:
                                    'Tooltip shown when link is copied to clipboard',
                                })
                              : intl.formatMessage({
                                  id: 'WorkflowTemplatesList.copyLinkTooltip',
                                  defaultMessage: 'Copy Intake Form Link',
                                  description: 'Tooltip for copy link button',
                                })
                          }
                          id={item.permanentLinkId}
                          onClick={() => handleOnCopyAction(item)}
                        />
                      </span>
                    </FlexLayout>
                  </FlexLayout>
                </FlexLayout>
              );
            })
        ) : (
          <Box p={6}>
            <Text color="gray-600" variant="xs-dense">
              <FormattedMessage
                id="WorkflowTemplatesList.noMatchingTemplates"
                defaultMessage="No workflow templates match your search."
                description="Message shown when no workflow templates match the search criteria"
              />
            </Text>
          </Box>
        )}
      </Box>
      <Paginate
        pageIndex={page}
        onUpdatePageIndex={setPage}
        totalCount={filteredItems.length}
        pageSize={PAGE_SIZE}
      />
    </FlexLayout>
  );
};

const mapStateToProps = ({ currentUser }) => ({ currentUser });

export default injectSheet(styles)(
  connect(mapStateToProps)(WorkflowTemplatesList),
);
